import React, { useState, useEffect } from 'react';
import './medico-legal-medical-ethics.css';

function MedicoLegalMedicalEthics() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [activePackageTab, setActivePackageTab] = useState('law-firms');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveTab(sectionId);
      setMobileMenuOpen(false);
    }
  };

  const lawFirmPackages = [
    {
      name: "ESSENTIAL PACKAGE",
      tagline: "Medico-Legal Case Intelligence",
      bestFor: "Small–medium law firms / individual cases",
      icon: "⚖️",
      features: [
        "Clinical record review",
        "Medical negligence screening",
        "Basic case strength assessment",
        "Identification of key medico-legal issues",
        "Preliminary opinion report"
      ],
      dataScience: [
        "Pattern-based case comparison",
        "Basic risk indicators"
      ],
      deliverables: [
        "5–10-page medico-legal analytical report",
        "Key legal risk flags",
        "Suggested case direction"
      ],
      value: "Quick case filtering • Reduced weak-case investment • Improved litigation strategy",
      price: "Per Case Basis",
      gradient: "linear-gradient(135deg, #1a1a2e, #16213e)"
    },
    {
      name: "ADVANCED PACKAGE",
      tagline: "Litigation Analytics & Risk Assessment",
      bestFor: "Complex cases / high-value litigation",
      icon: "📊",
      features: [
        "Full medico-legal analysis",
        "Injury & causation evaluation",
        "Standard of care assessment",
        "Compensation analysis"
      ],
      dataScience: [
        "Case outcome probability modelling",
        "Compensation trend analysis",
        "Comparative case analytics"
      ],
      deliverables: [
        "15–25-page expert analytical report",
        "Risk scoring model (case success probability)",
        "Data-supported compensation estimate",
        "Court-ready structured documentation"
      ],
      value: "Stronger legal positioning • Evidence-based argument building • Higher success probability",
      price: "Premium Case Basis",
      gradient: "linear-gradient(135deg, #1e3c72, #2a5298)"
    },
    {
      name: "PREMIUM PACKAGE",
      tagline: "AI-Enhanced Litigation Intelligence System",
      bestFor: "Large law firms / ongoing medico-legal practice",
      icon: "",
      features: [
        "Ongoing medico-legal advisory",
        "Multi-case portfolio analysis",
        "Strategic litigation planning"
      ],
      dataScience: [
        "AI-assisted case analysis system",
        "Predictive modelling for case outcomes",
        "Legal trend analytics dashboard"
      ],
      deliverables: [
        "Dedicated advisory support (monthly retainer)",
        "Custom litigation analytics dashboard",
        "Case database insights",
        "Continuous expert consultation"
      ],
      value: "Institutional competitive advantage • Faster decision-making • Scalable medico-legal intelligence",
      price: "Monthly Retainer",
      gradient: "linear-gradient(135deg, #2c3e50, #3498db)"
    },
    {
      name: "ENTERPRISE PACKAGE",
      tagline: "Law Firm Medico-Legal Data Transformation",
      bestFor: "Large firms / international legal groups",
      icon: "🏢",
      features: [
        "Full integration of medico-legal analytics",
        "Training + system development",
        "Case management intelligence"
      ],
      dataScience: [
        "Custom AI tools for case evaluation",
        "Data-driven litigation strategy frameworks",
        "Internal analytics systems"
      ],
      deliverables: [
        "Tailored software/dashboard solutions",
        "Staff training programs",
        "Long-term advisory partnership"
      ],
      value: "Market leadership positioning • Data-driven legal practice • High-value client acquisition",
      price: "Custom Enterprise Pricing",
      gradient: "linear-gradient(135deg, #800000, #a00000)"
    }
  ];

  const hospitalPackages = [
    {
      name: "ESSENTIAL PACKAGE",
      tagline: "Healthcare Performance Snapshot",
      bestFor: "Small hospitals / clinics",
      icon: "🏥",
      features: [
        "Basic hospital data review",
        "KPI analysis (admissions, outcomes, efficiency)",
        "Identification of operational gaps"
      ],
      dataScience: [
        "Descriptive analytics",
        "Basic trend visualization"
      ],
      deliverables: [
        "Performance report (10–15 pages)",
        "KPI dashboard (basic)",
        "Key improvement recommendations"
      ],
      value: "Immediate operational insights • Low-cost entry into data-driven management",
      gradient: "linear-gradient(135deg, #1a1a2e, #16213e)"
    },
    {
      name: "ADVANCED PACKAGE",
      tagline: "Operational Analytics & Risk Management System",
      bestFor: "Medium–large hospitals",
      icon: "📈",
      features: [
        "Clinical workflow analysis",
        "Patient outcome evaluation",
        "Risk identification (clinical + legal)",
        "Resource utilization assessment"
      ],
      dataScience: [
        "Predictive models for patient risk",
        "Efficiency optimization analytics",
        "Clinical error pattern detection"
      ],
      deliverables: [
        "Advanced analytics report",
        "Interactive dashboard (Power BI/Tableau)",
        "Risk assessment matrix",
        "Strategic improvement plan"
      ],
      value: "Reduced clinical risk • Improved efficiency • Better patient outcomes",
      gradient: "linear-gradient(135deg, #1e3c72, #2a5298)"
    },
    {
      name: "PREMIUM PACKAGE",
      tagline: "AI-Driven Hospital Intelligence System",
      bestFor: "Large private hospitals / healthcare chains",
      icon: "🧠",
      features: [
        "Full data-driven transformation",
        "Clinical decision support integration",
        "Medico-legal risk monitoring"
      ],
      dataScience: [
        "Patient risk prediction models",
        "AI-based clinical decision support",
        "Real-time hospital performance analytics"
      ],
      deliverables: [
        "Custom AI dashboards",
        "Risk monitoring system",
        "Predictive analytics tools",
        "Ongoing advisory support"
      ],
      value: "Proactive risk management • High-level executive control • Competitive advantage",
      gradient: "linear-gradient(135deg, #2c3e50, #3498db)"
    },
    {
      name: "ENTERPRISE PACKAGE",
      tagline: "Healthcare Data Transformation & AI Strategy",
      bestFor: "Hospital groups / international healthcare systems",
      icon: "🌍",
      features: [
        "Full digital transformation strategy",
        "AI system design and implementation",
        "Data governance and compliance"
      ],
      dataScience: [
        "Hospital-wide analytics infrastructure",
        "AI integration roadmap",
        "Staff training & system adoption"
      ],
      deliverables: [
        "End-to-end data ecosystem",
        "AI-powered healthcare platform",
        "Executive-level reporting systems"
      ],
      value: "Future-ready healthcare system • International standard operations • Scalable, data-driven growth",
      gradient: "linear-gradient(135deg, #800000, #a00000)"
    }
  ];

  const fivePillars = [
    { title: "Clinical Accuracy", desc: "Rigorous, evidence-based medical evaluation aligned with international clinical guidelines and standards of care.", icon: "🔬" },
    { title: "Legal Relevance", desc: "Clinical findings translated into legal language through negligence analysis, duty of care assessment, and causation mapping.", icon: "⚖️" },
    { title: "Structured Reasoning", desc: "Every conclusion follows logical progression, transparent justification, and evidence-backed inference.", icon: "🧩" },
    { title: "Strategic Utility", desc: "Outputs designed to support litigation strategy, strengthen negotiation positions, and enable informed decision-making.", icon: "🎯" },
    { title: "Objectivity & Integrity", desc: "Strict neutrality with no bias toward plaintiff or defense. Independent expert positioning and ethical compliance.", icon: "⭐" }
  ];

  const serviceArchitecture = [
    { number: "01", title: "Early Case Intelligence", desc: "Deep early-stage analysis to guide viability, positioning, and risk assessment before litigation begins.", highlight: "Avoid weak litigation • Build strong foundations" },
    { number: "02", title: "Expert Medico-Legal Reports", desc: "Court-ready reports as legal instruments, not medical summaries. Logically structured and cross-examination ready.", highlight: "Medical facts → Legal implications → Strategic argument" },
    { number: "03", title: "Litigation Strategy", desc: "Strategic partner to legal teams for case theory development, argument structuring, and cross-examination design.", highlight: "Stronger courtroom performance • Higher success potential" },
    { number: "04", title: "Independent Medical Opinions", desc: "Neutral, evidence-based second opinions for disputed diagnoses, alleged negligence, and insurance conflicts.", highlight: "Delivering clarity where conflict exists" },
    { number: "05", title: "Insurance & Claims Advisory", desc: "Structured medico-legal intelligence for claim validation, injury verification, fraud detection, and cost justification.", highlight: "Financial protection • Reduced fraud exposure" },
    { number: "06", title: "Hospital Risk Advisory", desc: "Clinical documentation audits, incident analysis, risk identification, and compliance evaluation.", highlight: "Reduced medico-legal exposure • Improved safety" }
  ];

  return (
    <div className="mlme-master-container">
      {/* Hero Section */}
     <section className="mlme-hero-section">

  {/* BACKGROUND IMAGE */}
  <img
    src="/medico-legal-banner.jpeg"
    alt="Medico Legal Banner"
    className="mlme-hero-bg"
  />

  <div className="mlme-hero-overlay"></div>
  <div className="mlme-hero-pattern"></div>

  <div className="mlme-hero-content">
    <div className="mlme-hero-badge">DR. JERY MEDICO (PVT) LTD</div>

    <h1 className="mlme-hero-title">
      Medico-Legal &<br />
      <span className="mlme-hero-highlight">
        Medical Ethics Division
      </span>
    </h1>

    <p className="mlme-hero-subtitle">
      Advanced Clinical–Legal Intelligence, Litigation Strategy & Risk Advisory
    </p>

    <div className="mlme-hero-quote">
      <p>Redefining Medico-Legal Practice in Sri Lanka</p>
    </div>

    <button
      className="mlme-hero-cta"
      onClick={() => scrollToSection('philosophy')}
    >
      Explore Our Intelligence System
      <span className="mlme-cta-arrow">→</span>
    </button>
  </div>

  <div className="mlme-hero-scroll">
    <div className="mlme-scroll-mouse"></div>
  </div>

</section>



      {/* Core Principle Section */}
      <section className="mlme-section mlme-principle-section">
        <div className="mlme-container">
          <div className="mlme-principle-card">
            <h2 className="mlme-principle-title">Our Core Principle</h2>
            <p className="mlme-principle-text">Effective medico-legal advisory must not only explain what happened — but also guide what should be argued, defended, or decided.</p>
            <div className="mlme-principle-divider"></div>
            <p className="mlme-principle-subtext">This principle defines everything we do.</p>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
<section id="philosophy" className="mlme-section mlme-philosophy-section">
  <div className="mlme-container">

    <div className="mlme-section-header">
      <span className="mlme-section-tag">Our Philosophy</span>
      <h2 className="mlme-section-title">From Reporting to Intelligence</h2>
      <div className="mlme-title-underline"></div>
    </div>

    <div className="mlme-philosophy-layout">

      {/* LEFT CONTENT */}
      <div className="mlme-philosophy-content">

        <p className="mlme-philosophy-text">
          In conventional systems, medico-legal reports are often static. They describe events but fail to connect clinical facts, legal standards, evidentiary weight, and strategic relevance.
        </p>

        <div className="mlme-philosophy-highlight">
          <span>
            At Dr. Jery Medico, we redefine medico-legal work as a multi-dimensional analytical process.
          </span>
        </div>

        <p className="mlme-philosophy-text">
          Every case is treated as a structured problem requiring the integration of:
        </p>

        <div className="mlme-integration-grid">
          <span className="mlme-integration-item">Medical Science</span>
          <span className="mlme-integration-plus">+</span>
          <span className="mlme-integration-item">Legal Doctrine</span>
          <span className="mlme-integration-plus">+</span>
          <span className="mlme-integration-item">Logical Reasoning</span>
          <span className="mlme-integration-plus">+</span>
          <span className="mlme-integration-item">Strategic Positioning</span>
        </div>

      </div>

      {/* RIGHT IMAGE */}
      <div className="mlme-image-wrapper">
        <img
          src="/medico-legal-advisory.jpeg"
          alt="Medico Legal Advisory"
          className="mlme-image"
        />
      </div>

    </div>

  </div>
</section>

      {/* Five Pillars Section */}
      <section id="pillars" className="mlme-section mlme-pillars-section">
        <div className="mlme-container">
          <div className="mlme-section-header">
            <span className="mlme-section-tag mlme-section-tag-light">The Foundation</span>
            <h2 className="mlme-section-title mlme-title-light">Five Pillars of Medico-Legal Intelligence</h2>
            <div className="mlme-title-underline mlme-underline-light"></div>
          </div>

          <div className="mlme-pillars-grid">
            {fivePillars.map((pillar, idx) => (
              <div key={idx} className="mlme-pillar-card">
                <div className="mlme-pillar-icon">{pillar.icon}</div>
                <div className="mlme-pillar-number">{String(idx + 1).padStart(2, '0')}</div>
                <h3 className="mlme-pillar-title">{pillar.title}</h3>
                <p className="mlme-pillar-desc">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>



{/* MID BANNER */}
<section className="mlme-mid-banner">

  <img
    src="/mid-first-banner.jpeg"
    alt="Medico Legal Banner"
    className="mlme-mid-banner-img"
  />
</section>






      {/* Service Architecture Section */}
      <section id="services" className="mlme-section mlme-service-section">
        <div className="mlme-container">
          <div className="mlme-section-header">
            <span className="mlme-section-tag">Service Architecture</span>
            <h2 className="mlme-section-title">Comprehensive Medico-Legal Solutions</h2>
            <div className="mlme-title-underline"></div>
            <p className="mlme-section-subtitle">Across the Entire Litigation Lifecycle</p>
          </div>

          <div className="mlme-service-grid">
            {serviceArchitecture.map((service) => (
              <div key={service.number} className="mlme-service-card">
                <div className="mlme-service-number">{service.number}</div>
                <h3 className="mlme-service-title">{service.title}</h3>
                <p className="mlme-service-desc">{service.desc}</p>
                <div className="mlme-service-highlight">{service.highlight}</div>
              </div>
            ))}
          </div>

          <div className="mlme-stakeholders-box">
            <h3>Who We Work With</h3>
            <div className="mlme-stakeholders-grid">
              <div className="mlme-stakeholder">
                <span className="mlme-stakeholder-icon">⚖️</span>
                <span>Legal Professionals</span>
              </div>
              <div className="mlme-stakeholder">
                <span className="mlme-stakeholder-icon">🏥</span>
                <span>Healthcare Providers</span>
              </div>
              <div className="mlme-stakeholder">
                <span className="mlme-stakeholder-icon">📋</span>
                <span>Insurance Companies</span>
              </div>
              <div className="mlme-stakeholder">
                <span className="mlme-stakeholder-icon">🏢</span>
                <span>Corporate Clients</span>
              </div>
            </div>
          </div>
        </div>
      </section>

{/* MID BANNER */}
<section className="mlme-mid-banner">

  <img
    src="/mid-banner-medico-legal.jpeg"
    alt="Medico Legal Banner"
    className="mlme-mid-banner-img"
  />
</section>



      {/* Service Packages Section */}
      <section id="packages" className="mlme-section mlme-packages-section">
        <div className="mlme-container">
          <div className="mlme-section-header">
            <span className="mlme-section-tag mlme-section-tag-light">Service Packages</span>
            <h2 className="mlme-section-title mlme-title-light">Data-Driven Medico-Legal Intelligence</h2>
            <div className="mlme-title-underline mlme-underline-light"></div>
            <p className="mlme-section-subtitle mlme-subtitle-light">For Litigation Advantage & Risk Optimization</p>
          </div>

          {/* Package Tabs */}
          <div className="mlme-package-tabs">
            <button 
              className={`mlme-package-tab ${activePackageTab === 'law-firms' ? 'active' : ''}`}
              onClick={() => setActivePackageTab('law-firms')}
            >
              <span>⚖️</span> For Law Firms
            </button>
            <button 
              className={`mlme-package-tab ${activePackageTab === 'hospitals' ? 'active' : ''}`}
              onClick={() => setActivePackageTab('hospitals')}
            >
              <span>🏥</span> For Healthcare Institutions
            </button>
          </div>

          {/* Law Firms Packages */}
          {activePackageTab === 'law-firms' && (
            <div className="mlme-packages-grid">
              {lawFirmPackages.map((pkg, idx) => (
                <div key={idx} className="mlme-package-card" style={{ background: pkg.gradient }}>
                  <div className="mlme-package-icon">{pkg.icon}</div>
                  <div className="mlme-package-badge">{pkg.bestFor}</div>
                  <h3 className="mlme-package-name">{pkg.name}</h3>
                  <p className="mlme-package-tagline">{pkg.tagline}</p>
                  
                  <div className="mlme-package-section">
                    <h4>Scope</h4>
                    <ul>
                      {pkg.features.map((feature, i) => <li key={i}>{feature}</li>)}
                    </ul>
                  </div>
                  
                  <div className="mlme-package-section">
                    <h4>Data Science Integration</h4>
                    <ul>
                      {pkg.dataScience.map((ds, i) => <li key={i}>📊 {ds}</li>)}
                    </ul>
                  </div>
                  
                  <div className="mlme-package-section">
                    <h4>Deliverables</h4>
                    <ul>
                      {pkg.deliverables.map((del, i) => <li key={i}>📄 {del}</li>)}
                    </ul>
                  </div>
                  
                  <div className="mlme-package-value">
                    <strong>Business Value:</strong> {pkg.value}
                  </div>
                  
                  <div className="mlme-package-price">
                    {pkg.price}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Hospitals Packages */}
          {activePackageTab === 'hospitals' && (
            <div className="mlme-packages-grid">
              {hospitalPackages.map((pkg, idx) => (
                <div key={idx} className="mlme-package-card" style={{ background: pkg.gradient }}>
                  <div className="mlme-package-icon">{pkg.icon}</div>
                  <div className="mlme-package-badge">{pkg.bestFor}</div>
                  <h3 className="mlme-package-name">{pkg.name}</h3>
                  <p className="mlme-package-tagline">{pkg.tagline}</p>
                  
                  <div className="mlme-package-section">
                    <h4>Scope</h4>
                    <ul>
                      {pkg.features.map((feature, i) => <li key={i}>{feature}</li>)}
                    </ul>
                  </div>
                  
                  <div className="mlme-package-section">
                    <h4>Data Science Integration</h4>
                    <ul>
                      {pkg.dataScience.map((ds, i) => <li key={i}>📊 {ds}</li>)}
                    </ul>
                  </div>
                  
                  <div className="mlme-package-section">
                    <h4>Deliverables</h4>
                    <ul>
                      {pkg.deliverables.map((del, i) => <li key={i}>📄 {del}</li>)}
                    </ul>
                  </div>
                  
                  <div className="mlme-package-value">
                    <strong>Business Value:</strong> {pkg.value}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* What Makes Us Unique */}
     <section className="mlme-section mlme-unique-section">

  <div className="mlme-container">

    <div className="mlme-unique-layout">

      {/* LEFT IMAGE */}
      <div className="mlme-unique-image">
        <img
          src="/medico-legal-dr-tiron.jpeg"
          alt="Medical Advisory Dr Jery"
        />
      </div>

      {/* RIGHT CONTENT */}
      <div className="mlme-unique-content">

        <div className="mlme-unique-badge">First-in-Sri Lanka</div>

        <h2 className="mlme-unique-title">What Makes Us Unique</h2>

        <div className="mlme-unique-underline"></div>

        <div className="mlme-unique-grid">
          <div className="mlme-unique-item">
            🎯 Multidisciplinary integration <span>(Medicine + Law + Management)</span>
          </div>

          <div className="mlme-unique-item">
            ⚡ Strategic—not descriptive—approach
          </div>

          <div className="mlme-unique-item">
            🧩 Structured analytical methodology
          </div>

          <div className="mlme-unique-item">
            🤝 Legal collaboration model
          </div>

          <div className="mlme-unique-item">
            🚀 Future integration with AI & predictive systems
          </div>
        </div>

        <p className="mlme-unique-statement">
          This is not evolution. This is transformation.
        </p>

      </div>

    </div>

  </div>

</section>



      {/* Value Proposition */}



      {/* Final Positioning */}
      <section className="mlme-final-section">
        <div className="mlme-container">
          <div className="mlme-final-content">
            <div className="mlme-final-quote">“</div>
            <p className="mlme-final-text">This is not a traditional medico-legal service.</p>
            <p className="mlme-final-highlight">It is a structured medico-legal intelligence and litigation support system — the first of its kind in Sri Lanka — built for the future of healthcare, law, and strategic decision-making.</p>
          </div>
        </div>
      </section>


    </div>
  );
}

export default MedicoLegalMedicalEthics;